import { useQuery } from "@tanstack/react-query";
import { useAtomValue } from "jotai";

import { currentUserInfoSchema } from "~/features/my-settings/schemas/user-info-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { getAndValidate } from "~/shared/services/api/fetch-client.ts";
import { selectedCustomerAtom } from "~/shared/state/user-profile-state.ts";

export function useGetCurrentUserInfo(isInternal = false) {
	const selectedCustomer = useAtomValue(selectedCustomerAtom);

	return useQuery({
		// External: the BFF resolves the user's role from
		// `Tql-Identity-Companyid` (set by the auth hook from
		// `selectedCustomerAtom`), and the role is *per customer* (e.g. admin
		// for customer A, vendor for customer B). Including the customer id in
		// the queryKey gives each customer its own cache entry so role updates
		// when the user swaps customers, instead of staying pinned to the
		// first customer's role.
		queryKey: [
			"currentUserInfo",
			isInternal,
			isInternal ? null : (selectedCustomer?.id ?? null),
		],
		// External users need `Tql-Identity-Companyid` injected by the auth
		// hook (from `selectedCustomerAtom`) for the BFF to resolve the
		// correct user/role. Defer firing until a customer is known so we
		// don't make a headerless first call on initial login that returns
		// the wrong role.
		enabled: isInternal || selectedCustomer?.id != null,
		queryFn: async () => {
			// `Tql-Identity-Companyid` and `Tql-Identity-Accounttype` are added
			// automatically by the default auth hook in fetch-client.ts from
			// `selectedCustomerAtom`, so we don't need an overrideHook here.
			const result = await getAndValidate(
				isInternal
					? apiRoutes.GETINTERNALCURRENTUSERINFO
					: apiRoutes.GETCURRENTUSERINFO,
				currentUserInfoSchema,
			);
			if (!result.success) {
				throw new Error(result.error);
			}
			return result.data;
		},
		// This query has many subscribers (Navbar, RoleGuard, page-level
		// hooks like useIsVendor, plus several feature pages). When the
		// request errors, TanStack Query's default retryOnMount: true causes
		// status to flip error -> pending every time a new subscriber mounts,
		// which flips `isLoading` back to true on gates like RoleGuard,
		// unmounts their children, and produces a refetch/flicker loop.
		// See repo memory: b2c-auth-inprogress-loop.md.
		refetchOnMount: false,
		retryOnMount: false,
	});
}
